import { useState } from "react";
import { AsyncSection } from "../components/AsyncSection";
import { Container } from "../components/Container";
import { SectionHeading } from "../components/SectionHeading";
import { Skeleton } from "../components/Skeleton";
import { useStaticResource } from "../hooks/useStaticResource";
import { trackedDealUrl } from "../lib/click";
import { safeExternalUrl } from "../lib/url";
import { VisuallyHidden } from "../components/VisuallyHidden";
import styles from "./FoundingWall.module.css";
import type { FoundingResponse, PublicDeal } from "../api/types";

/**
 * The Founding Deals wall — canon § 4, canon § 6, canon decision `D-009`.
 *
 * Canon § 4 makes the Founding badge permanent, but a badge is only visible on a deal that
 * is currently on a board, so during cold start it is a promise with nowhere to appear.
 * This section is the permanent place for the permanent thing: the first 10 approved deals,
 * named for as long as the page exists.
 *
 * **Three of its four defining properties were refusals**, and the two below still are; the
 * third — that it linked nowhere — was amended by `D-018` and is the first entry:
 *
 * - **It links to the deal, and that link ranks nothing** (`WEB-042`, canon decision
 *   `D-018` — this is the one refusal in `D-009` that has been amended). `D-009` allowed no
 *   `href` at all, on two grounds. The serious one was canon § 11: the weekly and monthly
 *   lists are ordered by qualified outbound clicks, and a permanent free click surface would
 *   have handed ten deals a standing advantage in the one ranking `AGENTS.md` § 14 says money
 *   must not be able to buy. `D-018` closes that at the ranking query instead — core excludes
 *   the `founding_wall` surface from both lists entirely — and accepts the second ground, that
 *   a free permanent link is exposure the auction sells, as a deliberate cost.
 *
 *   Two things follow for this file. The link goes through `trackedDealUrl` like every other
 *   call to action in this repo, because **the surface label is what makes the exclusion
 *   possible**: a raw `dealUrl` would record nothing and a mislabelled one would rejoin the
 *   rankings. And a *waiting* plaque links nowhere — there is no deal to open, and a
 *   clickable empty plaque is a control that does nothing.
 * - **It carries no rank, no bid, no price, no position and no placement source.** Core
 *   sends none of those on this read (`contracts/API.md` § `GET /v1/founding`) and this
 *   component renders none of the fields it does send that could imply one.
 * - **It never counts places down.** The label names the cohort and the wall's own contents
 *   show how full it is. `AGENTS.md` § 11 forbids fake scarcity, and a remaining-places
 *   figure is false the moment the next deal is approved. `cohort.size` is printed because
 *   it *names* the cohort; `cohort.filled` is deliberately not, because a reader can
 *   subtract.
 *
 * The fourth is read-time approval, which core applies for every public surface: a
 * disabled, rejected or re-review member is absent while that is true. Permanence is a
 * promise about cohort membership, not immunity from moderation.
 *
 * It sits at the **foot** of the page, after the argument has been made. `D-009` makes that
 * the load-bearing detail: directly beneath the sponsored board it would read as a second,
 * better board, and canon § 12's whole concern is a reader inferring editorial standing
 * from position. At the end, it reads as a credit line.
 *
 * ## One wall, in two states (`WEB-041`)
 *
 * This section used to render two different walls. Empty, it drew `cohort.size` waiting
 * plaques on a fixed five-across grid; populated, it dropped them entirely and laid the
 * members out on a different, auto-filling grid. So the first approval replaced a wall of ten
 * with a wall of one, and the section restyled itself on a moderation decision.
 *
 * It is now always `cohort.size` plaques: the members engraved, in permanent approval order,
 * and the rest waiting. A member who leaves under moderation leaves an unengraved plaque
 * behind rather than shrinking the wall, which is what "membership is permanent, exemption
 * from moderation is not" looks like when it is drawn.
 *
 * **This is not a countdown, and `D-009` is the reason it is not.** That decision refuses
 * copy that claims a number of remaining places, and says in the same paragraph that the
 * section "names the cohort and the wall's own contents show how full it is" — contents
 * showing fullness is precisely what it endorses. So ten plaques with three engraved is
 * allowed and `cohort.filled` in words still is not: the count line states the size and how
 * the wall fills, never how much of it is left.
 */
export function FoundingWall({ cohort }: { cohort: FoundingResponse }) {
  // Build-time data (`WEB-039`, canon `D-015`). The cohort is deliberately independent of
  // today — `GET /v1/founding` is the same list on every day, including the days none of its
  // members hold a position — which makes it exactly the kind of read a build can hold.
  const resource = useStaticResource(cohort);

  return (
    <section className={styles.section} aria-labelledby="founding">
      <Container>
        <SectionHeading
          id="founding"
          title="Founding Deals"
          description="Dealbid’s early supporters — the first deals approved here, credited permanently. This is recognition, not a ranking: nothing on this wall was bought, and a place on it is not a position on the board."
        />

        <AsyncSection
          state={resource.state}
          reload={resource.reload}
          loadingLabel="Loading the Founding Deals"
          errorTitle="The Founding Deals didn’t load"
          skeleton={<WallSkeleton />}
        >
          {(data) => {
            // Canon § 4's cap comes from core so no surface hardcodes ten (`CORE-027`).
            const waiting = Math.max(0, data.cohort.size - data.deals.length);

            return (
              <>
                <ul className={styles.wall} role="list">
                  {data.deals.map((deal) => (
                    <li key={deal.id} className={styles.cell}>
                      <Plaque deal={deal} />
                    </li>
                  ))}
                  {/*
                    The plaques nobody is on yet (`WEB-023`, `WEB-034`, kept in every state by
                    `WEB-041`). Decorative and `aria-hidden`, so the list a screen reader
                    counts is the members and never the cap; the count line below is what
                    tells that reader how full the wall is.

                    They carry **no ordinal**, which is where this departs from the canvas.
                    The canvas numbers them `01`–`10` in brass; this section's own heading
                    says a place here "is not a position on the board", `D-009` and `WEB-021`
                    both say the wall carries no rank and no position, and brass on this site
                    marks bought placement. Ten plaques still say ten. See
                    `contracts/design/web-homepage/README.md` departure C1.
                  */}
                  {Array.from({ length: waiting }, (_unused, index) => (
                    <li key={`waiting-${String(index)}`} className={styles.cell} aria-hidden="true">
                      {/*
                        A `span`, never an anchor. There is no deal to open, and a clickable
                        empty plaque is a control that does nothing (`WEB-042`).
                      */}
                      <span className={styles.plaque}>
                        <span className={styles.waitingMark} />
                        <span className={styles.engraving} />
                      </span>
                    </li>
                  ))}
                </ul>

                {/*
                  The canvas's count line, in the figure face. It states the cap and how the
                  wall fills, and never how much of it is left: `AGENTS.md` § 11 forbids fake
                  scarcity and `D-009` forbids a remaining-places figure, which would in any
                  case be false at the next approval. `cohort.filled` is deliberately not
                  printed — the plaques already show it.
                */}
                <p className={`${styles.count} figure`}>
                  {data.deals.length === 0
                    ? `${String(data.cohort.size)} plaques, none engraved yet.`
                    : data.deals.length >= data.cohort.size
                      ? `${String(data.cohort.size)} plaques, all engraved.`
                      : `${String(data.cohort.size)} plaques, engraved in the order deals are approved.`}
                </p>

                {data.deals.length > 0 ? (
                  /*
                    The cap, stated. Not "4 of 10", which is a remaining count a reader can do
                    in their head — `D-009` names the cohort and lets the wall's contents show
                    how full it is. The number comes from core so no surface hardcodes ten.
                  */
                  <p className={styles.footnote}>
                    The Founding cohort is Dealbid&rsquo;s first{" "}
                    <span className="figure">{String(data.cohort.size)}</span> approved deals,
                    in the order they were approved. Membership is permanent; a member under
                    review or disabled leaves this wall while that is true.
                  </p>
                ) : null}
              </>
            );
          }}
        </AsyncSection>
      </Container>
    </section>
  );
}

/**
 * One engraved plaque, linked to its deal (`WEB-042`, canon decision `D-018`).
 *
 * The whole card is the link rather than the name alone: it is a small target on a phone, and
 * a card whose text is clickable and whose padding is not is the kind of thing that only
 * misses for people with imprecise pointers.
 *
 * **The destination is `trackedDealUrl` and can be nothing else.** That is not a style rule —
 * `dealbid-web`'s `AGENTS.md` makes `src/lib/click.ts` the only place in this bundle allowed
 * to name a deal destination, and here the label it attaches is load-bearing beyond tracking:
 * `D-018` excludes the `founding_wall` surface from canon § 11's weekly and monthly lists, so
 * a link that named a different surface, or pointed straight at `dealUrl`, would put ten deals
 * back into the ranking money must not be able to buy.
 *
 * **A deal whose destination is unsafe renders an unlinked plaque**, not a broken link and not
 * an "unavailable" notice. `trackedDealUrl` runs `safeExternalUrl` over `dealUrl` and answers
 * `null` when core would refuse to redirect (canon `docs/SECURITY.md` § URLs). The board says
 * "Link unavailable" in that case because a deal with no call to action is a broken offer;
 * this wall is a credit line, and a member whose site has gone is still a member — the plaque
 * simply reads as it did before `D-018`.
 *
 * The accessible name is the product name plus where the link goes. Ten links all reading
 * "Founding Deal" would be a screen-reader failure the board's own rows already avoid.
 */
function Plaque({ deal }: { deal: PublicDeal }) {
  const href = trackedDealUrl(deal.id, "founding_wall", deal.dealUrl);

  const contents = (
    <>
      <Mark deal={deal} />
      {/*
        The name is the engraving. It sits where the waiting plaque draws its dotted line,
        which is what makes the two states one wall rather than two — and it is the whole of
        the entry: no rank, no bid, no price and no placement source (`D-009`, unamended on
        every point but the link).
      */}
      <p className={styles.name}>{deal.productName}</p>
    </>
  );

  if (href === null) return <span className={styles.plaque}>{contents}</span>;

  return (
    <a
      className={`${styles.plaque} ${styles.linked}`}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {contents}
      <VisuallyHidden>Open this deal from {deal.productName} (opens in a new tab)</VisuallyHidden>
    </a>
  );
}

/**
 * The member's brand mark: the logo where there is one, otherwise its initial.
 *
 * `logoUrl` is a response body and canon `AGENTS.md` § 12 treats that as untrusted input,
 * so it goes through the same `safeExternalUrl` gate as every other URL this bundle
 * renders — an `http`/`https` destination or nothing.
 *
 * A logo that *fails to load* falls back to the same initial, which is a separate case from
 * having none and is the more likely one: the URL is a founder's own host, and it can go
 * away, block hotlinking or simply 404 long after moderation checked it. The browser's
 * broken-image glyph is the one outcome this wall cannot have — a row of broken frames
 * beside ten product names says something about those products that is not true, on a
 * section whose entire purpose is to credit them.
 *
 * Decorative in both forms: the product name is the accessible content and sits directly
 * beside it, so a described image here would make a screen reader read every name twice.
 */
function Mark({ deal }: { deal: PublicDeal }) {
  const [unavailable, setUnavailable] = useState(false);
  const logo = safeExternalUrl(deal.logoUrl);

  if (logo === null || unavailable) {
    return (
      <span className={styles.mark} aria-hidden="true">
        {initial(deal.productName)}
      </span>
    );
  }

  return (
    <img
      className={styles.logo}
      src={logo}
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => {
        setUnavailable(true);
      }}
    />
  );
}

/**
 * The first character of a product name, as a reader sees it.
 *
 * Segmented by grapheme rather than sliced: `"🛰️Orbit".charAt(0)` is half a surrogate pair
 * and `Array.from` still splits an emoji from the variation selector that makes it one.
 * `src/lib/mask.ts` counts the same way and for the same reason — "character" here means
 * what a person perceives.
 */
const graphemes = new Intl.Segmenter(undefined, { granularity: "grapheme" });

function initial(productName: string): string {
  const [first] = [...graphemes.segment(productName.trim())];
  return first === undefined ? "·" : first.segment.toLocaleUpperCase();
}

function WallSkeleton() {
  return (
    <div className={styles.skeleton}>
      {[0, 1, 2, 3].map((cell) => (
        <Skeleton key={cell} height="3.25rem" />
      ))}
    </div>
  );
}
